import { computed, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { ulid } from 'ulid';
import {
  NotificationActionHandlers,
  NotificationItem,
  NotificationRef,
  SimpleNotification,
  SimpleNotificationInput,
  TaskNotification,
  TaskNotificationInput,
  TaskRef
} from './notification-center.types';

@Injectable({
  providedIn: 'root'
})
export class NotificationCenterService {
  readonly drawerOpen = signal(false);
  readonly items = signal<NotificationItem[]>([]);

  readonly unreadCount = computed(() =>
    this.visibleItems().filter((item) => item.unread).length
  );

  readonly visibleItems = computed(() =>
    this.items()
      .filter((item) => !item.dismissedAt)
      .slice()
      .sort((left, right) => {
        const activeDelta = Number(this.isActive(right)) - Number(this.isActive(left));
        if (activeDelta !== 0) {
          return activeDelta;
        }

        return right.updatedAt - left.updatedAt;
      })
  );

  readonly activeItems = computed(() =>
    this.visibleItems().filter((item) => this.isActive(item))
  );

  readonly runningTasks = computed(() =>
    this.visibleItems().filter(
      (item): item is TaskNotification => item.type === 'task' && this.isRunningTask(item)
    )
  );

  readonly completedItems = computed(() =>
    this.visibleItems().filter((item) => !this.isActive(item))
  );

  private readonly actionHandlers = new Map<string, NotificationActionHandlers>();

  constructor(private readonly router: Router) {}

  push(config: SimpleNotificationInput): NotificationRef {
    const now = Date.now();

    const item: SimpleNotification = {
      id: ulid(),
      type: 'simple',
      kind: config.kind,
      title: config.title,
      message: config.message,
      createdAt: config.createdAt ?? now,
      updatedAt: now,
      unread: config.unread ?? true,
      dismissible: config.dismissible ?? true,
      route: config.route,
      trackingKey: config.trackingKey,
      dismissedAt: null
    };

    this.items.update((items) => [item, ...items]);
    this.registerHandlers(item.id, { onOpen: config.onOpen });

    if (config.openOnCreate) {
      this.openDrawer();
    }

    return this.createNotificationRef(item.id);
  }

  startTask(config: TaskNotificationInput): TaskRef {
    const now = Date.now();

    const item: TaskNotification = {
      id: ulid(),
      type: 'task',
      kind: config.kind,
      title: config.title,
      message: config.message,
      createdAt: config.createdAt ?? now,
      updatedAt: now,
      unread: config.unread ?? true,
      dismissible: config.dismissible ?? true,
      route: config.route,
      trackingKey: config.trackingKey,
      dismissedAt: null,
      status: config.status ?? 'queued',
      cancellable: config.cancellable ?? false,
      progress: config.progress,
      entries: config.entries,
      actionLabel: config.actionLabel,
      actionDisabled: config.actionDisabled,
      actionTooltip: config.actionTooltip
    };

    this.items.update((items) => [item, ...items]);
    this.registerHandlers(item.id, {
      onOpen: config.onOpen,
      onCancel: config.onCancel
    });

    if (config.openOnCreate ?? true) {
      this.openDrawer();
    }

    return this.createTaskRef(item.id);
  }

  remove(id: string): void {
    this.items.update((items) => items.filter((item) => item.id !== id));
    this.actionHandlers.delete(id);
  }

  dismiss(id: string): void {
    this.updateItem(id, {
      dismissedAt: Date.now(),
      unread: false
    });
  }

  markAsRead(id: string): void {
    this.updateItem(id, { unread: false });
  }

  markAllAsRead(): void {
    this.items.update((items) =>
      items.map((item) => {
        if (item.dismissedAt || !item.unread) {
          return item;
        }

        return {
          ...item,
          unread: false,
          updatedAt: Date.now()
        };
      })
    );
  }

  openDrawer(): void {
    this.drawerOpen.set(true);
    this.markAllAsRead();
  }

  closeDrawer(): void {
    this.drawerOpen.set(false);
  }

  toggleDrawer(): void {
    if (this.drawerOpen()) {
      this.closeDrawer();
      return;
    }

    this.openDrawer();
  }

  async openItem(id: string): Promise<void> {
    const item = this.items().find((current) => current.id === id);
    if (!item) {
      return;
    }

    if (item.type === 'task' && item.actionDisabled) {
      return;
    }

    this.markAsRead(id);

    const handlers = this.actionHandlers.get(id);
    if (handlers?.onOpen) {
      await handlers.onOpen();
      return;
    }

    if (item.route) {
      await this.router.navigateByUrl(item.route);
    }
  }

  async cancelTask(id: string): Promise<void> {
    const handlers = this.actionHandlers.get(id);
    await handlers?.onCancel?.();
  }

  upsertByTrackingKey(
    trackingKey: string,
    patch: Partial<NotificationItem>
  ): void {
    const item = this.findByTrackingKey(trackingKey);
    if (!item) {
      return;
    }

    this.updateItem(item.id, patch);
  }

  findByTrackingKey(trackingKey: string): NotificationItem | undefined {
    return this.items().find((item) => item.trackingKey === trackingKey);
  }

  private createNotificationRef(id: string): NotificationRef {
    return {
      id,
      update: (patch) => this.updateItem(id, patch),
      remove: () => this.remove(id)
    };
  }

  private createTaskRef(id: string): TaskRef {
    return {
      id,
      update: (patch) => this.updateItem(id, patch),
      updateEntry: (entryId, patch) => this.updateTaskEntry(id, entryId, patch),
      complete: (patch) =>
        this.updateItem(id, {
          ...patch,
          status: 'success',
          cancellable: false,
          unread: true
        }),
      fail: (message, patch) =>
        this.updateItem(id, {
          ...patch,
          status: 'error',
          message: message ?? patch?.message,
          cancellable: false,
          unread: true
        }),
      cancel: () => this.cancelTask(id),
      remove: () => this.remove(id)
    };
  }

  private registerHandlers(id: string, handlers: NotificationActionHandlers): void {
    const current = this.actionHandlers.get(id) ?? {};
    this.actionHandlers.set(id, {
      onOpen: handlers.onOpen ?? current.onOpen,
      onCancel: handlers.onCancel ?? current.onCancel
    });
  }

  private updateTaskEntry(id: string, entryId: string, patch: Record<string, unknown>): void {
    const item = this.items().find((current) => current.id === id);
    if (!item || item.type !== 'task') {
      return;
    }

    const entries = (item.entries ?? []).map((entry) =>
      entry.id === entryId ? { ...entry, ...patch } : entry
    );

    this.updateItem(id, { entries });
  }

  private updateItem(id: string, patch: Partial<NotificationItem>): void {
    this.items.update((items) =>
      items.map((item) => {
        if (item.id !== id) {
          return item;
        }

        return {
          ...item,
          ...patch,
          updatedAt: Date.now()
        } as NotificationItem;
      })
    );
  }

  private isActive(item: NotificationItem): boolean {
    if (item.type === 'task') {
      return this.isRunningTask(item);
    }

    return item.unread;
  }

  private isRunningTask(item: TaskNotification): boolean {
    return item.status === 'queued' || item.status === 'running';
  }
}
